import React, { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { WebView, WebViewMessageEvent } from 'react-native-webview';
import { useTranslation } from 'react-i18next';

import { ResumeDocument } from '../../types';
import { Palette, spacing, useThemePalette } from '../../theme';
import { templateRegistry } from '../../lib/resume';

interface Props {
  resume: ResumeDocument;
}

const ResumePreview: React.FC<Props> = ({ resume }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [loading, setLoading] = useState(true);

  const labels = useMemo(
    () => ({
      contact: t('resume.sections.contact'),
      summary: t('resume.sections.summary'),
      experience: t('resume.sections.experience'),
      education: t('resume.sections.education'),
      skills: t('resume.sections.skills'),
      languages: t('resume.sections.languages'),
      links: t('resume.sections.links'),
      present: t('resume.fields.present'),
    }),
    [t]
  );

  // Re-render whenever any rendered field changes. Using a JSON snapshot of
  // the fields the template actually reads keeps the WebView cheap when the
  // user types in the resume name field, which doesn't affect the body.
  const html = useMemo(
    () => templateRegistry[resume.template].toHtml(resume, labels, resume.personalization),
    [resume, labels]
  );

  return (
    <View style={styles.container}>
      <WebView
        originWhitelist={['*']}
        source={{ html }}
        onLoadEnd={() => setLoading(false)}
        onMessage={(_e: WebViewMessageEvent) => undefined}
        javaScriptEnabled={false}
        domStorageEnabled={false}
        scalesPageToFit
        style={styles.web}
      />
      {loading && (
        <View style={styles.loader} pointerEvents="none">
          <ActivityIndicator color={palette.primary} />
        </View>
      )}
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    // Reserve enough vertical room for a printable resume in the editor's
    // available area. The WebView itself scrolls; this height is big enough
    // to feel like a page on phones, and the user can scroll inside it.
    container: {
      height: 560,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: p.surface,
      borderWidth: 1,
      borderColor: p.border,
      marginTop: spacing.xs,
    },
    web: { flex: 1, backgroundColor: 'transparent' },
    loader: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

export default ResumePreview;
